/**
 * Pen-Tool Drawing Hook.
 *
 * Manages the draft vertices while a user traces a shape on the facility-map
 * canvas. Clicking appends a vertex; moving the pointer tracks a live
 * "rubber-band" segment to the cursor.
 *
 * Two geometries:
 * - "polygon" (default): clicking near the first vertex (once ≥3 exist) closes
 *   the shape and fires `onComplete`.
 * - "polyline": stays open; the caller finishes it explicitly via `finish()`
 *   (e.g. double-click or Enter), requiring ≥2 vertices.
 *
 * The hook is pure state + handlers — all coordinate conversion (screen → map
 * units) happens in the canvas that owns the <svg>, which passes already
 * converted map-unit points in here.
 */

import { useCallback, useEffect, useState } from "react";
import { segmentLength, type Point } from "./shapeUtils";

interface UseBoundaryDrawOptions {
  /** Whether draw mode is active. Toggling off clears any in-progress draft. */
  active: boolean;
  /** Distance (in map units) to the first vertex that counts as "closing". */
  closeThreshold: number;
  /** Geometry being traced: closed polygon or open polyline. */
  geometry?: "polygon" | "polyline";
  /** Called with the finished vertex list when the shape is completed. */
  onComplete: (points: Point[]) => void;
}

/** Drop a trailing vertex that coincides with the previous one (double-click). */
function dropCoincidentTail(points: Point[]): Point[] {
  if (points.length >= 2) {
    const a = points[points.length - 1]!;
    const b = points[points.length - 2]!;
    if (segmentLength(a, b) < 0.001) return points.slice(0, -1);
  }
  return points;
}

export function useBoundaryDraw({
  active,
  closeThreshold,
  geometry = "polygon",
  onComplete,
}: UseBoundaryDrawOptions) {
  const [draft, setDraft] = useState<Point[]>([]);
  const [cursor, setCursor] = useState<Point | null>(null);

  // Clear the draft whenever draw mode is turned off.
  useEffect(() => {
    if (!active) {
      setDraft([]);
      setCursor(null);
    }
  }, [active]);

  /** Add a vertex, or (polygon) close the shape if the click lands on start. */
  const addPoint = useCallback(
    (p: Point) => {
      if (
        geometry === "polygon" &&
        draft.length >= 3 &&
        segmentLength(draft[0]!, p) <= closeThreshold
      ) {
        onComplete(draft);
        setDraft([]);
        setCursor(null);
        return;
      }
      setDraft((prev) => [...prev, p]);
    },
    [geometry, draft, closeThreshold, onComplete],
  );

  /** Explicitly finish an open (polyline) chain, or close a polygon. */
  const finish = useCallback(() => {
    const pts = dropCoincidentTail(draft);
    const min = geometry === "polyline" ? 2 : 3;
    if (pts.length >= min) {
      onComplete(pts);
      setDraft([]);
      setCursor(null);
    }
  }, [draft, geometry, onComplete]);

  /** Track the pointer to render the live rubber-band segment. */
  const moveCursor = useCallback((p: Point) => setCursor(p), []);

  /** Remove the most recently placed vertex. */
  const undo = useCallback(() => setDraft((prev) => prev.slice(0, -1)), []);

  /** Discard the whole in-progress draft. */
  const cancel = useCallback(() => {
    setDraft([]);
    setCursor(null);
  }, []);

  /** Whether the pointer is within closing distance of the start (polygon). */
  const canClose =
    geometry === "polygon" &&
    draft.length >= 3 &&
    cursor != null &&
    segmentLength(draft[0]!, cursor) <= closeThreshold;

  return {
    draft,
    cursor,
    addPoint,
    finish,
    moveCursor,
    undo,
    cancel,
    canClose,
  };
}
